import React, { useState } from "react";
import { useCompany } from "../context/CompanyContext";
import { getNodeApiUrl } from "../config/api";
import { useNavigate } from "react-router-dom";
const CompanySetup = () => {
  const { refreshCompany } = useCompany();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    website: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const validateForm = () => {
    const errors = {};
    // Name is required
    if (!formData.name.trim()) {
      errors.name = "Organization name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Organization name must be at least 2 characters";
    }
    // Website is optional, but if provided, must be valid URL
    if (formData.website && !isValidUrl(formData.website)) {
      errors.website = "Please enter a valid website URL";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("accessToken");
      // Validate authentication before proceeding
      if (!token) {
        setError("Authentication required. Please log in again.");
        return;
      }
      const response = await fetch(getNodeApiUrl("/api/companies"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          description: "No description provided",
          website: formData.website.trim() || null,
          logo: null,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess("🎉 Organization created successfully! Redirecting...");
        // Refresh company context
        await refreshCompany();

        // Set flag to show welcome preview after redirect
        localStorage.setItem("show_welcome_preview", "true");

        // Redirect to create page after a short delay
        setTimeout(() => {
          navigate("/create");
        }, 1000);
      } else {
        if (response.status === 409) {
          setError(
            "You already have an organization! Redirecting to create page...",
          );
          setTimeout(() => {
            navigate("/create");
          }, 2000);
        } else {
          setError(
            data.error || "Failed to create organization. Please try again.",
          );
        }
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to Qudemo! 🚀
          </h2>
          <p className="text-gray-600 mb-8">
            Let's set up your organization to get started
          </p>
        </div>
      </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Company Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Organization Name *
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    formErrors.name ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter your organization name"
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>
            </div>
            {/* Website */}
            <div>
              <label
                htmlFor="website"
                className="block text-sm font-medium text-gray-700"
              >
                Website (Optional)
              </label>
              <div className="mt-1">
                <input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    formErrors.website ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="https://yourorganization.com (optional)"
                />
                {formErrors.website && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.website}
                  </p>
                )}
              </div>
            </div>
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}
            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                {success}
              </div>
            )}
            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center h-10 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? "Creating Organization..."
                  : "Create Organization & Continue"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default CompanySetup;
